import {
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { organizationRepository } from "api/organizationRepository";
import { useEffect, useState } from "react";
import { departmentSchema, flattenZodErrors } from "validations/Validations";
import { z } from "zod";

interface EditDepartmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  branches: any[];
  department: any | null;
  onDepartmentUpdated: (updatedBranches: any[]) => void;
  showCenteredAlert: (msg: string, status?: "success" | "info" | "warning" | "error") => void;
}

export default function EditDepartmentModal({
  isOpen,
  onClose,
  organizationId,
  branches,
  department,
  onDepartmentUpdated,
  showCenteredAlert,
}: EditDepartmentModalProps) {
  const [editDept, setEditDept] = useState<any | null>(department);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const cardBg = useColorModeValue('white', 'navy.800');

  useEffect(() => {
    setEditDept(department);
    setErrors({});
  }, [department]);

  const handleUpdate = async () => {
    if (!editDept) return;

    const branch = branches.find(b => b.locationId === editDept.locationId);

    const payload = {
      departmentName: editDept.departmentName || "",
      locationId: editDept.locationId || "",
      locationName: branch?.name || "",
    };

    try {
      departmentSchema.parse(payload);
      setErrors({});
      setIsUpdating(true);

      await organizationRepository.updateDepartment(
        organizationId,
        editDept.locationId,
        editDept.departmentId,
        {
          departmentId: editDept.departmentId,
          departmentName: editDept.departmentName,
          departmentLocation: branch?.name || "",
        }
      );

      const updatedOrg = await organizationRepository.getOrganizationById(organizationId);
      onDepartmentUpdated(updatedOrg?.locations || []);
      showCenteredAlert("Department updated successfully", "success");
      onClose();
    } catch (err) {
      if (err instanceof z.ZodError) {
        setErrors(flattenZodErrors(err));
        showCenteredAlert("Please fix the validation errors.", "warning");
      } else {
        showCenteredAlert("Failed to update department", "error");
      }
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent bg={cardBg}>
        <ModalHeader>Edit Department</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={4}>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Plant</FormLabel>
              <Select value={editDept?.locationId || ""} isDisabled>
                {branches.map((branch: any) => (
                  <option key={branch.locationId} value={branch.locationId}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Department Name</FormLabel>
              <Select
                placeholder="Select Department"
                value={editDept?.departmentName || ""}
                onChange={(e) =>
                  setEditDept({ ...editDept, departmentName: e.target.value })
                }
              >
                {(() => {
                  if (!editDept) return null;
                  const branch = branches.find((b: any) => b.locationId === editDept.locationId);
                  const allDepartments = ["Melting", "Moulding", "Production", "Maintenance", "Lab"];
                  const availableDepartments = allDepartments.filter(
                    (dept) =>
                      dept.toLowerCase() === editDept.departmentName?.toLowerCase() ||
                      !branch?.departments?.some(
                        (d: any) => d.departmentName.toLowerCase() === dept.toLowerCase()
                      )
                  );
                  return availableDepartments.length > 0 ? (
                    availableDepartments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))
                  ) : (
                    <option disabled>No departments available</option>
                  );
                })()}
              </Select>
              {errors.departmentName && (
                <Text fontSize="xs" color="red.500">{errors.departmentName}</Text>
              )}
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleUpdate}
            isLoading={isUpdating}
          >
            Update
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
